// client/src/components/secure/OutsideSitesTab.js — Property Search → Outside sites.
//
// One set of criteria, one button per listing site; each opens that site's own search in a
// new browser tab (utils/outsideSearch). Nothing is fetched or copied by the portal. A
// property worth keeping goes into the client's Site search with "Add a listing" (paste) or
// the "Send to BREC" bookmark. The managing broker can edit the site links.
import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { Card, ErrorLine, Btn, input } from './territory/Shared';
import { DEFAULT_SITES, buildUrl, mergeSites } from '../../utils/outsideSearch';

const API = '/api/property-search/outside-sites';
const label = 'block text-xs font-medium text-slate-600 mb-1';

const OutsideSitesTab = ({ isBroker }) => {
    const api = useApiClient();
    const [sites, setSites] = useState(DEFAULT_SITES);
    const [c, setC] = useState({ kind: 'residential', deal: 'sale', city: '', state: '', beds: '', baths: '', min: '', max: '' });
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get(API).then((r) => {
            setSites(mergeSites(r.sites));
            if (r.default_state) setC((x) => (x.state ? x : { ...x, state: r.default_state }));
        }).catch(() => {});
    }, [api]);

    const shown = useMemo(() => sites.filter((s) => s.kind === c.kind), [sites, c.kind]);
    const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
    const ready = c.city.trim() && c.state.trim().length === 2;

    const saveLinks = async () => {
        setError('');
        try {
            const r = await api.put(API, { sites: editing });
            setSites(mergeSites(r.sites)); setEditing(null);
        } catch (e) { setError(e.message || 'Could not save the links.'); }
    };

    return (
        <div className="space-y-4">
            <Card title="Search outside sites">
                <p className="text-sm text-slate-600 mb-3">
                    Until the MLS feed is connected: enter the client's criteria once, then open each site's search with one click.
                    The search runs in your own browser on the site itself. Price, bed and bath filters carry over where the site's address supports them;
                    on the others, set them on the site.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><label className={label}>Property</label>
                        <select className={input} value={c.kind} onChange={set('kind')}>
                            <option value="residential">Residential</option><option value="commercial">Commercial</option>
                        </select></div>
                    <div><label className={label}>Deal</label>
                        <select className={input} value={c.deal} onChange={set('deal')}>
                            <option value="sale">For sale</option><option value="rent">{c.kind === 'commercial' ? 'For lease' : 'For rent'}</option>
                        </select></div>
                    <div><label className={label}>City</label><input className={input} value={c.city} onChange={set('city')} placeholder="Oak Park" /></div>
                    <div><label className={label}>State</label><input className={input} value={c.state} onChange={set('state')} maxLength={2} placeholder="IL" /></div>
                    {c.kind === 'residential' && <>
                        <div><label className={label}>Beds (min)</label><input className={input} value={c.beds} onChange={set('beds')} inputMode="numeric" /></div>
                        <div><label className={label}>Baths (min)</label><input className={input} value={c.baths} onChange={set('baths')} inputMode="numeric" /></div>
                    </>}
                    <div><label className={label}>Price min</label><input className={input} value={c.min} onChange={set('min')} inputMode="numeric" /></div>
                    <div><label className={label}>Price max</label><input className={input} value={c.max} onChange={set('max')} inputMode="numeric" /></div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                    {shown.map((s) => {
                        const url = ready ? buildUrl(c.deal === 'sale' ? s.sale : s.rent, c) : '';
                        const offered = !!(c.deal === 'sale' ? s.sale : s.rent);
                        if (!offered) return null;
                        return url
                            ? <a key={s.key} href={url} target="_blank" rel="noopener noreferrer"
                                className="px-3 py-1.5 text-sm rounded-md bg-indigo-700 text-white hover:bg-indigo-800">{s.label} ↗</a>
                            : <span key={s.key} className="px-3 py-1.5 text-sm rounded-md bg-slate-100 text-slate-400">{s.label}</span>;
                    })}
                </div>
                {!ready && <p className="text-xs text-slate-500 mt-2">Enter a city and a two-letter state to turn the buttons on.</p>}
            </Card>

            <Card title="Found one for a client?">
                <p className="text-sm text-slate-600">
                    Open the client → <b>Site search</b> → <b>Add a listing</b> and paste the listing's address (link) and page text,
                    or click the <b>Send to BREC</b> bookmark while on the listing. The portal fills in the details for you to check,
                    marks where it came from, and keeps it in the client's file. Outside listings are never shown on the public website.
                </p>
            </Card>

            {isBroker && (
                <Card title="Site links" right={!editing && <Btn kind="secondary" onClick={() => setEditing(sites.map((s) => ({ ...s })))}>Edit links</Btn>}>
                    {!editing && <p className="text-sm text-slate-500">If a site changes its addresses and a button lands on the wrong page, fix its link here.</p>}
                    {editing && (
                        <div className="space-y-3">
                            <p className="text-xs text-slate-500">
                                Placeholders: {'{city_slug}'} oak-park · {'{City_dash}'} Oak-Park · {'{City_ST}'} Oak-Park_IL · {'{st}'} IL · {'{st_lower}'} il ·
                                {' {beds} {baths} {min} {max}'} · {'{min_na} {max_na}'} ("na" when blank). A part in [brackets] is left out when its values are blank.
                                Leave a link empty to hide that button.
                            </p>
                            {editing.map((s, i) => (
                                <div key={s.key} className="grid md:grid-cols-[10rem_1fr_1fr] gap-2 items-center">
                                    <div className="text-sm font-medium">{s.label} <span className="text-xs text-slate-400">({s.kind})</span></div>
                                    <input className={input} value={s.sale} placeholder="For sale link"
                                        onChange={(e) => setEditing(editing.map((x, j) => (j === i ? { ...x, sale: e.target.value } : x)))} />
                                    <input className={input} value={s.rent} placeholder="For rent / lease link"
                                        onChange={(e) => setEditing(editing.map((x, j) => (j === i ? { ...x, rent: e.target.value } : x)))} />
                                </div>
                            ))}
                            <ErrorLine error={error} />
                            <div className="flex gap-2">
                                <Btn onClick={saveLinks}>Save links</Btn>
                                <Btn kind="secondary" onClick={() => setEditing(DEFAULT_SITES.map((s) => ({ ...s })))}>Reset to standard links</Btn>
                                <Btn kind="secondary" onClick={() => setEditing(null)}>Cancel</Btn>
                            </div>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
};

export default OutsideSitesTab;
